import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { db } from "../firebase";
import { 
  doc, 
  getDoc, 
  updateDoc, 
  addDoc, 
  collection, 
  serverTimestamp,
  query,
  where,
  getDocs,
  limit 
} from "firebase/firestore";
import { getAuth } from 'firebase/auth';
import { ChatBubbleLeftRightIcon, ChevronLeftIcon, UserCircleIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import { getFunctions, httpsCallable } from "firebase/functions";

const SendMessage = () => {
  console.log('=== RENDERING SENDMESSAGE COMPONENT ===');
  
  const navigate = useNavigate();
  const location = useLocation();
  const auth = getAuth();

  const { verse = {}, discipleId, userAppID } = location.state || {};
  const [customMessage, setCustomMessage] = useState("");
  const [customQuestion, setCustomQuestion] = useState(verse?.question || "");
  const [includeQuestion, setIncludeQuestion] = useState(true);
  const [messageType, setMessageType] = useState(['sms']);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [smsLaunched, setSmsLaunched] = useState(false);
  const [discipleData, setDiscipleData] = useState(null);
  const [mentorDetails, setMentorDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [activeReminder, setActiveReminder] = useState(null);
  const [loadingHeader, setLoadingHeader] = useState(true);

  const getEffectiveQuestion = () => (includeQuestion ? customQuestion : '');
  const formatMessageForPreview = () => {
    const parts = [];
    if (customMessage.trim()) parts.push(customMessage.trim());
    if (verse?.reference && verse?.text) parts.push(`${verse.reference}\n${verse.text}`);
    if (getEffectiveQuestion().trim()) parts.push(getEffectiveQuestion().trim());
    return parts.join('\n\n');
  };
  const hasContent = customMessage.trim() || (verse?.reference && verse?.text) || getEffectiveQuestion().trim();
  const generateSMSLink = () => {
    const parts = [];
    if (customMessage.trim()) parts.push(customMessage.trim());
    if (verse?.reference && verse?.text) parts.push(`${verse.reference}\n${verse.text}`);
    if (getEffectiveQuestion().trim()) parts.push(getEffectiveQuestion().trim());
    const messageBody = parts.join('\n\n');
    const encodedMessage = encodeURIComponent(messageBody);
    const cleanPhone = discipleData?.phone?.replace(/\D/g, '') || '';
    return `sms:${cleanPhone}?body=${encodedMessage}`;
  };

  // Fetch disciple data and mentor details
  useEffect(() => {
    const fetchHeaderData = async () => {
      if (!discipleId || !userAppID) {
        console.warn("SendMessage: Missing discipleId or userAppID in location state.")
        setLoadingHeader(false);
        return;
      }
      setLoadingHeader(true);
      try {
        const [discipleDoc, mentorDetailsDoc] = await Promise.all([
            getDoc(doc(db, 'disciples', discipleId)),
            getDoc(doc(db, 'disciples', discipleId, 'mentorDetails', userAppID))
        ]);

        if (discipleDoc.exists()) {
          setDiscipleData(discipleDoc.data());
        } else {
          console.warn("SendMessage: Disciple document not found for header.")
        }
        if (mentorDetailsDoc.exists()) {
            setMentorDetails(mentorDetailsDoc.data());
        } else {
           console.warn("SendMessage: Mentor details document not found for header.")
        }
      } catch (error) {
        console.error('Error fetching header data:', error);
      } finally {
        setLoadingHeader(false);
      }
    };

    fetchHeaderData();
  }, [discipleId, userAppID]);

  // Email sending function
  const sendEmailMessage = async () => {
    console.log("Starting modern email send process...");
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error("No user logged in");
    }

    try {
      // Get mentor's details for name and reply-to email
      const userDoc = await getDoc(doc(db, "users", currentUser.uid));
      const mentorName = userDoc.data()?.displayName || "Your Mentor";
      const mentorEmail = currentUser.email;

      if (!discipleData?.email) {
        throw new Error("Disciple's email address is missing.");
      }

      // Construct the rich payload for the email
      const messagePayload = {
        // Target recipient
        userEmail: discipleData.email,
        discipleName: discipleData.name || "Disciple",

        // Message content
        customMessage: customMessage.trim(),
        verseReference: verse.reference,
        verseText: verse.text,
        reflectionQuestion: customQuestion.trim(),

        // Sender and reply-to details
        replyTo: mentorEmail,
        mentorName: mentorName,

        // Specify message type
        messageType: ["email"],
      };

      const functions = getFunctions();
      const sendMessageFn = httpsCallable(functions, "sendMessage");
      
      console.log('Calling "sendMessage" Cloud Function with payload:', messagePayload);
      const result = await sendMessageFn(messagePayload);
      console.log("Modern email sent successfully:", result.data);
      
      return result.data;
    } catch (error) {
      console.error("Error sending email via modern setup:", error);
      throw error;
    }
  };

  // Add this helper function at the top level
  const updateReminderDates = async (discipleId, userAppID) => {
    try {
      const mentorRef = doc(db, 'disciples', discipleId, 'mentorDetails', userAppID);
      const mentorDoc = await getDoc(mentorRef);
      const currentData = mentorDoc.data();
      
      if (currentData?.reminderSettings) {
        const now = new Date();
        const frequency = currentData.reminderSettings.frequency || 7;
        const nextDate = new Date(now.getTime() + (frequency * 24 * 60 * 60 * 1000));

        await updateDoc(mentorRef, {
          'reminderSettings.lastMessageDate': now.toISOString(),
          'reminderSettings.nextReminderDate': nextDate.toISOString()
        });
      }
    } catch (error) {
      console.error('Error updating reminder dates:', error);
    }
  };

  // Helper function to store message in Firestore
  const storeMessage = async (discipleId, userAppID, messageData, type) => {
    const collectionName = type === 'sms' ? 'messagesSMS' : 'messages';
    const messagesRef = collection(
      db, 
      'disciples', 
      discipleId, 
      'mentorDetails', 
      userAppID, 
      collectionName
    );

    return addDoc(messagesRef, {
      ...messageData,
      type,
      sentAt: serverTimestamp(),
      createdAt: serverTimestamp()
    });
  };

  // Update handleEmailOnly
  const handleEmailOnly = async () => {
    try {
      setIsLoading(true);
      
      // Store message first
      await storeMessage(discipleId, userAppID, {
        verseReference: verse.reference,
        text: verse.text,
        question: getEffectiveQuestion(),
        customMessage: customMessage,
      }, 'email');
      
      // Then send email
      await sendEmailMessage();
      
      // Update reminder dates and status
      await updateReminderDates(discipleId, userAppID);
      await updateReminderStatus();
      
      navigate(`/disciple-menu/${discipleId}`, { replace: true }); // Force a fresh load
    } catch (error) {
      console.error('Email send failed:', error);
      alert('Failed to send email');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSMSOnly = async () => {
    const smsUrl = generateSMSLink();
    window.open(smsUrl, '_blank');
    
    // Store SMS message in Firestore
    try {
      await storeMessage(discipleId, userAppID, {
        verseReference: verse.reference,
        text: verse.text,
        question: getEffectiveQuestion(),
        customMessage: customMessage,
      }, 'sms');
    } catch (error) {
      console.error('Failed to store SMS message:', error);
    }
    
    setShowConfirmModal(true);
  };

  const handleBothTypes = async () => {
    try {
      setIsLoading(true);
      const smsUrl = generateSMSLink();
      window.open(smsUrl, '_blank');
      
      // Store both message types first
      await Promise.all([
        storeMessage(discipleId, userAppID, {
          verseReference: verse.reference,
          text: verse.text,
          question: getEffectiveQuestion(),
          customMessage: customMessage,
        }, 'email'),
        storeMessage(discipleId, userAppID, {
          verseReference: verse.reference,
          text: verse.text,
          question: getEffectiveQuestion(),
          customMessage: customMessage,
        }, 'sms')
      ]);

      // Then send email
      if (messageType.includes('email')) {
        await sendEmailMessage();
      }
      
      // Update reminder dates and status
      await updateReminderDates(discipleId, userAppID);
      await updateReminderStatus();
      
      setShowConfirmModal(false);
      navigate(`/disciple-menu/${discipleId}`, { replace: true }); // Force a fresh load
    } catch (error) {
      console.error('Error:', error);
      alert('Failed to send message. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const renderActionButton = () => {
    if (messageType.length === 0) {
      return (
        <button
          type="button"
          disabled
          className="w-full px-6 py-3 rounded-lg text-white bg-gray-500 cursor-not-allowed text-sm font-medium"
        >
          Select a message type
        </button>
      );
    }

    if (messageType.includes('email') && !messageType.includes('sms')) {
      return (
        <button
          type="button"
          onClick={handleEmailOnly}
          disabled={isLoading}
          className="w-full px-6 py-3 rounded-lg text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed text-sm font-medium transition-colors duration-200"
        >
          {isLoading ? 'Sending...' : 'Send Email'}
        </button>
      );
    }

    if (messageType.includes('sms') && !messageType.includes('email')) {
      return (
        <button
          type="button"
          onClick={handleSMSOnly}
          disabled={isLoading}
          className="w-full px-6 py-3 rounded-lg text-white bg-green-600 hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed text-sm font-medium transition-colors duration-200"
        >
          Send SMS
        </button>
      );
    }

    return (
      <button
        type="button"
        onClick={handleBothTypes}
        disabled={isLoading}
        className="w-full px-6 py-3 rounded-lg text-white bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 disabled:cursor-not-allowed text-sm font-medium transition-colors duration-200"
      >
        {isLoading ? 'Sending...' : 'Send Email & SMS'}
      </button>
    );
  };

  useEffect(() => {
    const fetchActiveReminder = async () => {
      if (!discipleId || !auth.currentUser) {
        console.log('🔔 SendMessage - Missing discipleId or user:', { discipleId, user: auth.currentUser?.uid });
        return;
      }
      
      try {
        console.log('🔔 SendMessage - Fetching active reminder for disciple:', discipleId);
        const reminderQueueRef = collection(db, 'users', auth.currentUser.uid, 'reminderQueue');
        const activeQuery = query(
          reminderQueueRef,
          where('discipleId', '==', discipleId),
          where('queueStatus', 'in', ['sent', 'pending']),
          where('reminderStatus', 'in', ['unread', 'wait', 'inProgress']),
          limit(1)
        );
        
        const snapshot = await getDocs(activeQuery);
        console.log('🔔 SendMessage - Query results:', { 
          empty: snapshot.empty, 
          size: snapshot.size 
        });

        if (!snapshot.empty) {
          const reminder = {
            id: snapshot.docs[0].id,
            ...snapshot.docs[0].data()
          };
          setActiveReminder(reminder);
          console.log('🔔 SendMessage - Active Reminder Found:', {
            id: reminder.id,
            status: reminder.reminderStatus,
            discipleName: reminder.discipleName,
            scheduledFor: reminder.scheduledFor?.toDate(),
          });
        } else {
          console.log('🔔 SendMessage - No active reminders found for this disciple');
        }
      } catch (error) {
        console.error('Error fetching active reminder:', error);
      }
    };

    fetchActiveReminder();
  }, [discipleId, auth.currentUser]);

  const updateReminderStatus = async () => {
    console.log('🔔 SendMessage - Attempting to update reminder status:', { 
      reminderId: activeReminder?.id,
      userId: auth.currentUser?.uid 
    });
    
    if (!activeReminder?.id || !auth.currentUser) {
      console.log('🔔 SendMessage - No active reminder or user, skipping update');
      return;
    }
    
    try {
      const reminderRef = doc(db, 'users', auth.currentUser.uid, 'reminderQueue', activeReminder.id);
      await updateDoc(reminderRef, {
        reminderStatus: 'completed',
        completedAt: serverTimestamp(),
        messageSent: true,
        messageSentAt: serverTimestamp(),
        messageType: messageType,
        messageDetails: {
          verseReference: verse?.reference,
          customMessage: customMessage.trim() || null,
          customQuestion: getEffectiveQuestion().trim() || null
        }
      });
      console.log('🔔 SendMessage - Reminder status and message details updated successfully');
    } catch (error) {
      console.error('Error updating reminder status:', error);
    }
  };

  // Add this function before your component
  const updateReminderQueue = async (discipleId, discipleName, currentUser) => {
    try {
      // Get the mentor details to get the frequency
      const mentorRef = doc(db, 'disciples', discipleId, 'mentorDetails', currentUser.uid);
      const mentorDoc = await getDoc(mentorRef);
      const mentorData = mentorDoc.data();
      
      if (!mentorData?.reminderSettings?.enabled) return;

      const frequency = mentorData.reminderSettings.frequency;
      const now = new Date();
      const nextDate = new Date(now);
      nextDate.setDate(nextDate.getDate() + frequency);

      // Create new reminder
      const reminderData = {
        discipleId,
        discipleName,
        mentorId: currentUser.uid,
        message: `Time to connect with ${discipleName}`,
        scheduledFor: nextDate,
        queueStatus: 'queued',
        reminderStatus: 'scheduled',
        archived: false,
        frequency,
        interval: frequency,
        preferredTime: mentorData.reminderSettings.preferredTime,
        notificationMethod: mentorData.reminderSettings.notificationMethod,
        attempts: 0,
        messageSent: false,
        notificationsSent: 0,
        createdAt: now,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      };

      // Add to reminder queue
      const reminderQueueRef = collection(db, 'users', currentUser.uid, 'reminderQueue');
      await addDoc(reminderQueueRef, reminderData);

      // Update the mentor details with new next reminder date
      await updateDoc(mentorRef, {
        'reminderSettings.lastMessageDate': now.toISOString(),
        'reminderSettings.nextReminderDate': nextDate.toISOString()
      });
    } catch (error) {
      console.error('Error updating reminder queue:', error);
    }
  };

  if (loadingHeader) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center">Loading...</div>;
  }

  if (!discipleData || !verse || !userAppID) {
    return (
        <div className="container mx-auto mt-6 p-6">
            <p className="text-red-500">Error: Missing required information to send a message. Please go back and select a verse again.</p>
            <button onClick={() => navigate(-1)} className="mt-4 px-4 py-2 bg-blue-500 text-white rounded">Go Back</button>
        </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Updated Header to match drawer pages */}
      <div className="bg-white border-b">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center min-w-0 flex-1">
            <button
              onClick={() => navigate(-1)}
              className="mr-3 p-1 rounded-full hover:bg-gray-100"
              aria-label="Go back"
            >
              <ChevronLeftIcon className="h-6 w-6 text-gray-600" />
            </button>

            <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden flex-shrink-0">
              {mentorDetails?.photoURL ? (
                <img
                  src={mentorDetails.photoURL}
                  alt={discipleData?.name || 'Disciple'}
                  className="h-full w-full object-cover"
                />
              ) : (
                <UserCircleIcon className="h-8 w-8 text-gray-400" />
              )}
            </div>

            <div className="ml-3 min-w-0">
              <h1 className="text-lg font-semibold text-gray-900 truncate">
                Personalize Message
              </h1>
              <p className="text-sm text-gray-500 truncate">
                for {discipleData?.name || 'your disciple'}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-4">
        {/* Message type: SMS | Email | Both (mobile-style pills) */}
        <div className="flex bg-white rounded-xl p-1.5 border border-gray-200 shadow-sm mb-4">
          <button
            type="button"
            onClick={() => setMessageType(['sms'])}
            className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg text-sm font-semibold transition ${
              messageType.length === 1 && messageType[0] === 'sms'
                ? 'bg-green-600 text-white'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <ChatBubbleLeftRightIcon className="h-5 w-5" />
            SMS
          </button>
          <button
            type="button"
            onClick={() => setMessageType(['email'])}
            className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg text-sm font-semibold transition ${
              messageType.length === 1 && messageType[0] === 'email'
                ? 'bg-blue-600 text-white'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <EnvelopeIcon className="h-5 w-5" />
            Email
          </button>
          <button
            type="button"
            onClick={() => setMessageType(['sms', 'email'])}
            className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg text-sm font-semibold transition ${
              messageType.length === 2
                ? 'bg-indigo-600 text-white'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            Both
          </button>
        </div>

        {/* Personal message first (like mobile) */}
        <div className="mb-4">
          <label htmlFor="customMessage" className="block text-sm font-medium text-gray-700 mb-1.5">
            Personal message (optional)
          </label>
          <textarea
            id="customMessage"
            value={customMessage}
            onChange={(e) => setCustomMessage(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
            rows={4}
            placeholder="Write a personal message..."
          />
        </div>

        {/* Selected verse (read-only block + change link) */}
        <div className="mb-4 p-4 bg-white border border-gray-200 rounded-xl shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Verse</span>
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="text-sm text-blue-600 hover:underline"
            >
              Change verse
            </button>
          </div>
          {verse?.reference && <p className="font-semibold text-gray-900">{verse.reference}</p>}
          {verse?.text && <p className="text-gray-700 text-sm mt-1 whitespace-pre-wrap">{verse.text}</p>}
        </div>

        {/* Reflection question with Include toggle (like mobile) */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-sm font-medium text-gray-700">Reflection question</label>
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <span className="text-sm text-gray-500">Include</span>
              <input
                type="checkbox"
                checked={includeQuestion}
                onChange={(e) => setIncludeQuestion(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
            </label>
          </div>
          <textarea
            id="customQuestion"
            value={customQuestion}
            onChange={(e) => setCustomQuestion(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
            rows={2}
            placeholder="Add a question for reflection..."
          />
        </div>

        {/* Message preview — only when there's content (mobile-style bubble) */}
        {hasContent && (
          <div className="mb-6">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Message preview</p>
            <div className="flex justify-start">
              <div className="bg-[#007AFF] text-white px-4 py-3 rounded-2xl rounded-br-md max-w-[85%] shadow-sm">
                <p className="text-sm whitespace-pre-wrap break-words leading-relaxed">{formatMessageForPreview()}</p>
              </div>
            </div>
          </div>
        )}

        {/* Single Send button */}
        <div className="w-full">{renderActionButton()}</div>

        {showConfirmModal && ( 
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
            <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-sm mx-auto">
              <h3 className="text-lg font-semibold mb-3 text-gray-900">Message Prepared</h3>
              <p className="mb-6 text-gray-600 text-sm">Did you successfully send the SMS message?</p>
              <div className="space-y-3">
                <button
                  onClick={() => {
                    console.log('No, trying again');
                    setShowConfirmModal(false);
                  }}
                  className="w-full px-4 py-3 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg text-sm font-medium transition-colors duration-200"
                  disabled={isLoading}
                >
                  No, Try Again
                </button>
                <button
                  onClick={async () => {
                    try {
                      setIsLoading(true);
                      if (messageType.includes('email')) {
                        await sendEmailMessage();
                      }
                      
                      try {
                        // Update reminder dates for both email and SMS
                        await updateReminderDates(discipleId, userAppID);
                        
                        // Update reminder status if one exists
                        await updateReminderStatus();
                      } catch (updateError) {
                        console.error('Error updating details:', updateError);
                      }
                      
                      setShowConfirmModal(false);
                      navigate(`/disciple-menu/${discipleId}`);
                    } catch (error) {
                      console.error('Error:', error);
                      alert('Failed to send message. Please try again.');
                    } finally {
                      setIsLoading(false);
                    }
                  }}
                  className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed flex items-center justify-center text-sm font-medium transition-colors duration-200"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending...
                    </>
                  ) : (
                    'Yes, Continue'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SendMessage;

